import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import NoData, { hasData } from "../../../common/NoData";
import { YesNoDonut, YesNoCards } from "../../../common/YesNoCharts";
import DashboardBarChart from "../../../common/DashboardBarChart";
import { BRAND } from "../../../../utils/chartTheme";

const AssetsTab = ({ filters, fetchData, navigate, dispatch }) => {
  const [data, setData] = useState({
    assetOwnership: [],
    femaleCellPhone: { yes: 0, no: 0, total: 0 },
    agriculturalLand: { yes: 0, no: 0, total: 0 },
    landAcres: {
      mean: 0,
      min: 0,
      max: 0,
      median: 0,
      std: 0,
      bins: [],
    },
    livestock: [],
    bankAccount: { yes: 0, no: 0, total: 0 },
    mobileAccount: { yes: 0, no: 0, total: 0 },
  });
  const [loading, setLoading] = useState(true);

  const formatCount = (num) =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";

  const formatPercent = (part, total) => {
    if (!total || total === 0) return "0.0";
    return ((part / total) * 100).toFixed(1);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          assetRes,
          femaleCellRes,
          agriLandRes,
          landAcresRes,
          livestockRes,
          bankRes,
          mobileRes,
        ] = await Promise.all([
          fetchData(
            "asset-ownership-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/asset-ownership-stats",
            dispatch
          ),
          fetchData(
            "female-cell-phone-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/female-cell-phone-stats",
            dispatch
          ),
          fetchData(
            "agricultural-land-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/agricultural-land-stats",
            dispatch
          ),
          fetchData(
            "land-acres-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/land-acres-stats",
            dispatch
          ),
          fetchData(
            "livestock-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/livestock-stats",
            dispatch
          ),
          fetchData(
            "bank-account-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/bank-account-stats",
            dispatch
          ),
          fetchData(
            "mobile-account-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/mobile-account-stats",
            dispatch
          ),
        ]);

        setData({
          assetOwnership: assetRes.data || [],
          femaleCellPhone: femaleCellRes.data || { yes: 0, no: 0, total: 0 },
          agriculturalLand: agriLandRes.data || { yes: 0, no: 0, total: 0 },
          landAcres: landAcresRes.data || {
            mean: 0,
            min: 0,
            max: 0,
            median: 0,
            std: 0,
            bins: [],
          },
          livestock: livestockRes.data || [],
          bankAccount: bankRes.data || { yes: 0, no: 0, total: 0 },
          mobileAccount: mobileRes.data || { yes: 0, no: 0, total: 0 },
        });
      } catch (err) {
        console.error("Error loading Assets tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch, fetchData]);

  // Asset Ownership Chart
  const totalAssetHH = data.assetOwnership.length > 0
    ? data.assetOwnership[0].total_households || 0
    : 0;

  const assetSeries = [
    {
      name: "Households",
      data: data.assetOwnership.map((item) => item.count || 0),
    },
  ];

  // Female Cell Phone Pie Chart
  const femaleCellPhonePieOptions = {
    chart: { type: "pie" },
    plotOptions: { pie: { dataLabels: { minAngleToShowLabel: 18 } } },
    labels: ["Households with Female Cell Phone", "Households without Female Cell Phone"],
    colors: [BRAND.teal, BRAND.gold],
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "13px",
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "14px", fontWeight: "bold" },
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} households`,
      },
    },
  };

  const femaleCellPhoneSeries = [
    data.femaleCellPhone.yes,
    data.femaleCellPhone.no,
  ];

  const renderAgriculturalLandDonut = () => (
    <YesNoDonut
      yes={data.agriculturalLand.yes}
      no={data.agriculturalLand.no}
      total={data.agriculturalLand.total}
      yesLabel="With Land"
      noLabel="Without Land"
    />
  );
  const renderBankAccountCards = () => (
    <YesNoCards
      yes={data.bankAccount.yes}
      no={data.bankAccount.no}
      yesLabel="With Bank Account"
      noLabel="Without Bank Account"
    />
  );
  const renderMobileAccountDonut = () => (
    <YesNoDonut
      yes={data.mobileAccount.yes}
      no={data.mobileAccount.no}
      total={data.mobileAccount.total}
      yesLabel="With Mobile"
      noLabel="Without Mobile"
    />
  );

  const has = {
    agriculturalLand: hasData(data.agriculturalLand.yes, data.agriculturalLand.no),
    bankAccount: hasData(data.bankAccount.yes, data.bankAccount.no),
    mobileAccount: hasData(data.mobileAccount.yes, data.mobileAccount.no),
    livestock: hasData(data.livestock.map((item) => item.total_count)),
    femaleCellPhone: hasData(data.femaleCellPhone.yes, data.femaleCellPhone.no),
    landAcres: hasData(data.landAcres),
    assetOwnership: hasData(assetSeries[0].data),
  };

  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;

  return (
    <div className="space-y-6 pb-8">
      {!loading && !Object.values(has).some(Boolean) && (
        <NoData className="py-16" />
      )}
      {/* Row 1: Agricultural Land, Bank Account, Mobile Account */}
      {showRow(has.agriculturalLand, has.bankAccount, has.mobileAccount) && (<>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agricultural Land Ownership - Donut */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance mb-4">
            Land Ownership
          </h3>
          {panel(has.agriculturalLand, renderAgriculturalLandDonut())}
        </div>

        {/* Bank Account Ownership - Card Style */}
        <div className="bg-white flex flex-col h-full">
          <div className="w-full">
            <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance mb-4">
              Bank Account Ownership
            </h3>
            {panel(has.bankAccount, renderBankAccountCards())}
          </div>
        </div>

        {/* Mobile Account Ownership - Donut */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance mb-4">
            Mobile Account Ownership
          </h3>
          {panel(has.mobileAccount, renderMobileAccountDonut())}
        </div>
      </div>
      </>)}

      {/* Row 2: Livestock Ownership Table and Female Cell Phone Pie Chart */}
      {showRow(has.livestock, has.femaleCellPhone) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Livestock Ownership Table */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Livestock Ownership
          </h3>
          <table className="w-full text-base mt-2 border-collapse">
            <thead>
              <tr className="bg-teal-700 text-white">
                <th className="p-2 text-left rounded-tl-xl">Animal Type</th>
                <th className="p-2 text-center">Total Count</th>
                <th className="p-2 text-center rounded-tr-xl">Average per Household</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {data.livestock.length > 0 ? (
                data.livestock.map((item, idx) => (
                  <tr
                    key={idx}
                    className={`border-b ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-100`}
                  >
                    <td className="p-2 font-semibold">{item.animal_type}</td>
                    <td className="text-center font-bold text-lg text-teal-700">
                      {formatCount(item.total_count)}
                    </td>
                    <td className="text-center font-bold text-lg text-teal-700">
                      {item.average_per_hh}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-8 text-gray-500">
                    No Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Right: Female Cell Phone Pie Chart */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Households with Female Cell Phone
          </h3>
          <div className="flex items-center justify-center h-full">
            {data.femaleCellPhone.total > 0 ? (
              <ReactApexChart
                options={femaleCellPhonePieOptions}
                series={femaleCellPhoneSeries}
                type="pie"
                height={300}
              />
            ) : (
              <NoData />
            )}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 3: Land Acreage Statistics */}
      {showRow(has.landAcres) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="bg-white flex flex-col h-full">
        <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
          Land Acreage Statistics
        </h3>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6 mt-2">
          {[
            { label: "Average (Acres)", value: data.landAcres.mean },
            { label: "Minimum (Acres)", value: data.landAcres.min },
            { label: "Maximum (Acres)", value: data.landAcres.max },
            { label: "Median (Acres)", value: data.landAcres.median },
            { label: "Std Deviation", value: data.landAcres.std },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]"
            >
              <p className="text-2xl font-extrabold text-[#137E81]">
                {stat.value}
              </p>
              <p className="text-xs text-[#0B5557] mt-2 font-semibold">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Distribution Chart */}
        <div>
          <h4 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Distribution by Acreage Range
          </h4>
          <DashboardBarChart
            items={data.landAcres.bins.map((b) => ({
              name: b.range,
              count: b.count || 0,
            }))}
            unit="households"
          />
        </div>
      </div>
      </>)}

      {/* Row 4: Asset Ownership */}
      {showRow(has.assetOwnership) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="bg-white flex flex-col h-full">
        <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
          Asset Ownership
        </h3>
        <p className="text-center text-sm text-gray-600 mb-4">
          Number and Percentage of Households Owning Various Assets
        </p>
        <div className="mb-4 text-center">
          <span className="text-sm font-semibold text-teal-700">
            Total Households: {formatCount(totalAssetHH)}
          </span>
        </div>
  
        <DashboardBarChart
          horizontal
          allowList={false}
          fold={false}
          height={800}
          items={data.assetOwnership.map((item) => ({
            name: item.asset_name,
            count: item.count || 0,
     
            percentage: formatPercent(item.count || 0, totalAssetHH),
          }))}
          unit="households"
        />
      </div>
      </>)}
    </div>
  );
};

export default AssetsTab;